import React, { useRef } from "react";
import {
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  Modal,
  Icon,
  Input,
  isNullish
} from "@theorgchartdev/storybook";

import FileDirectory from "../../Composites/FileDirectory/FileDirectory";
import CentralizedModalContainer from "../../Utils/CentralizedModalContainer/CentralizedModalContainer";
import { useFileManagerContext } from "@/providers/FileManagerProvider";
import { GetWSMgr } from "@/Utils/StateLib";
import { appstr } from "@coreapp/utils/appstr";
import { enumFileMgrMode } from "@coreapp/constants";
import { ValidateRename } from "@coreapp/utils/ValidateRename";
import { IsBackgroundFile } from "@coreapp/utils/IsBackgroundFile";
import { StripExtension } from "@coreapp/utils/StripExtension";

const FileManagerModal = ({ onCustomSelect, ...props }) => {
  const { fileManager, contextValue } = useFileManagerContext();
  const [selectedFileObj, setSelectedFileObj] = React.useState(null);
  const [fileDirectory, setFileDirectory] = React.useState(null);
  const [customFilename, setCustomFilename] = React.useState(contextValue.defaultInputFilename);
  const [selectDisabled, setSelectDisabled] = React.useState(contextValue.selectDisabled);
  const [nameError, setNameError] = React.useState("");
  const inputRef = useRef(null);

  const wsMgr = GetWSMgr();
  const docMgr = wsMgr?.GetDocMgr();

  // use effect that updates the content state prop that re-renders when fileManager or context value changes
  React.useEffect(() => {
    if (contextValue.showFileManager) setFileDirectory(GetRenderContent());
    if (customFilename == null) {
      setCustomFilename(getDefaultInputName());
    }
  }, [fileManager, contextValue]);

  const getDefaultInputName = () => {
    const backgroundSaveMode = inSaveBackgroundMode();
    let defaultName = contextValue.defaultInputFilename ?? appstr("Untitled");
    let curFilename = docMgr?.GetFileName();
    const doc = docMgr?.GetCurrentDoc();
    const chartMeta = doc?.GetIWizData();
    if (backgroundSaveMode) {
      const backgroundFile = chartMeta.GetMasterFile();
      if (!isNullish(backgroundFile) && IsBackgroundFile(backgroundFile)) {
        curFilename = StripExtension(backgroundFile);
      } else {
        curFilename = defaultName;
      }
    }
    if (!curFilename || curFilename.length < 1) return defaultName;
    return curFilename;
  };

  const inSaveBackgroundMode = () => {
    return fileManager?.state?.GetSaveAsMaster() === true;
  };

  // wrapper for referencing file director
  const GetRenderContent = () => {
    return <FileDirectory onFileClick={onFileManagerClick} onFileSelect={onFileManagerSelect} />;
  };

  const confirmFileManagerExit = cmd => {
    wsMgr.DoExitFileMgr(null, cmd);
  };

  const onClickHelp = () => {
    if (contextValue.helpCode) wsMgr.LaunchHelp(contextValue.helpCode);
  };

  // handle file clicks and setting current selection states (coming from the FileDirectory component)
  const onFileManagerClick = fileObj => {
    let targetFileName = fileObj?.GetFileName() ?? null;
    setSelectedFileObj(fileObj);
    setCustomFilename(targetFileName);
    if (fileObj != null) {
      inputRef?.current?.select();
    }
    setSelectDisabled(false);
  };

  // double click handling (coming from FileDirectory component)
  const onFileManagerSelect = fileObj => {
    onFileManagerClick(fileObj);
    if (contextValue?.onSelectCommand) confirmFileManagerExit(contextValue.onSelectCommand);
  };

  // input handler for changing file names ( only applicable in save modes )
  const handleInput = e => {
    let inputTarget = e.target;
    setCustomFilename(inputTarget.value);
    fileManager.state.fSaveAsTextEditName = customFilename;
    if (nameError) setNameError("");
  };

  const handleSelect = () => {
    if (contextValue?.showFilenameInput && customFilename != null) {
      const { valid, message } = ValidateRename(customFilename, wsMgr);
      if (!valid) {
        setNameError(message);
        return;
      }
    }
    confirmFileManagerExit(contextValue?.onSelectCommand);
  };

  const selectButtonLabel = contextValue.selectButtonString || appstr("Select");
  const fileInputPlaceholder = [enumFileMgrMode.savemaster].includes(fileManager?.state?.GetMode())
    ? appstr("Name your background")
    : appstr("Name your chart");

  // rendering content from FileDirectory component state prop
  return (
    <CentralizedModalContainer show={contextValue?.showFileManager}>
      <div className={"ocn-chart-explorer-wrapper"}>
        <Modal
          {...props}
          id="example-modal"
          show={contextValue?.showFileManager}
          centerScreen={true}
          onClose={() => confirmFileManagerExit(contextValue?.onCloseCommand)}
          fullScreen={true}
        >
          <DialogTitle
            divider={true}
            onClose={() => confirmFileManagerExit(contextValue?.onCloseCommand)}
          >
            <span className={"ocn-explorer-icon-label"}>
              <Icon size={"medium"} name={"folder-regular"} />
              {contextValue?.title}
            </span>
          </DialogTitle>
          <DialogContent customContentClass={"ocn-file-manager-content"}>
            <div className="ocn-file-manager">{fileDirectory}</div>
            {contextValue?.showFilenameInput && (
              <div style={{ padding: "0px 16px 16px" }}>
                <Input
                  ref={inputRef}
                  autoFocus={true}
                  id="ocn-filemanager-name-input"
                  label={appstr("Name")}
                  message={nameError || undefined}
                  placeholder={fileInputPlaceholder}
                  size="medium"
                  status={nameError ? "error" : "default"}
                  type="text"
                  style={{}}
                  onFocus={() => inputRef?.current?.select()}
                  onChange={handleInput}
                  value={customFilename}
                />
              </div>
            )}
          </DialogContent>
          {
            <DialogActions divider={true}>
              <DialogActions.Tertiary
                onClick={() => confirmFileManagerExit(contextValue?.onCloseCommand)}
                dataTestId={"fm-cancel-btn"}
              >
                {appstr("Cancel")}
              </DialogActions.Tertiary>
              <DialogActions.Primary
                onClick={handleSelect}
                dataTestId={`fm-${selectButtonLabel}-btn`}
                disabled={selectDisabled}
              >
                {selectButtonLabel}
              </DialogActions.Primary>
            </DialogActions>
          }
        </Modal>
      </div>
    </CentralizedModalContainer>
  );
};

export default FileManagerModal;
